// ProfileChip.jsx — E3 (super-marathon) multi-profile: switch + CREATE + EDIT + Copy-setup.
//
// The persona control in the UserMenu. Each persona has: name, model, INSTRUCTIONS (system
// prompt / "who am I"), MEMORY (persistent notes / SOUL), and skills. You can create, EDIT
// (all fields, incl. memory), switch (activating adopts the persona's model + instructions +
// memory in the turn — "acorda a identidade"), and Copy-setup (export/import as portable JSON).
// Visible whenever apolo_multi_profile is on (default), even with zero profiles.

import { useEffect, useRef, useState } from 'react';
import { Check, Plus, Pencil, Copy, ClipboardPaste, X } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore.js';
import { readMultiProfileEnabled } from '../lib/flags/sprintFlags.js';

const EMPTY_FORM = { name: '', model_id: '', instructions: '', memory: '', color: '' };

// 8-color palette (mirrors the folder colors) for the profile's first-letter avatar.
const PROFILE_COLORS = ['#f43f5e', '#f59e0b', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899'];

// The profile's identity mark: its first LETTER on a soft tint of its color (neutral when no
// color). Replaces the generic user icon in the chip + the list rows.
function LetterAvatar({ name, color, size = 16, active = false }) {
  const letter = ((name || '?').trim()[0] || '?').toUpperCase();
  const c = color || '';
  return (
    <span
      aria-hidden="true"
      className={'inline-flex shrink-0 items-center justify-center rounded-md font-semibold ' + (active ? 'ring-1 ring-apolo-textPrimary/50' : '')}
      style={{
        width: size, height: size, fontSize: Math.round(size * 0.56),
        backgroundColor: c ? `${c}2e` : 'var(--apolo-surface3)',
        color: c || 'var(--apolo-textSecondary)',
      }}
    >
      {letter}
    </span>
  );
}

export default function ProfileChip() {
  const enabled = readMultiProfileEnabled();
  const profiles = useAuthStore((s) => s.profiles);
  const activeProfileId = useAuthStore((s) => s.activeProfileId);
  const fetchProfiles = useAuthStore((s) => s.fetchProfiles);
  const switchProfile = useAuthStore((s) => s.switchProfile);
  const createProfile = useAuthStore((s) => s.createProfile);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const exportProfile = useAuthStore((s) => s.exportProfile);
  const importProfile = useAuthStore((s) => s.importProfile);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // null | 'create' | 'edit' | 'import'
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [importText, setImportText] = useState('');
  const [note, setNote] = useState('');
  const ref = useRef(null);

  useEffect(() => { if (enabled) fetchProfiles(); }, [enabled, fetchProfiles]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) closeAll(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!enabled) return null;

  const list = Array.isArray(profiles) ? profiles : [];
  const active = list.find((p) => p.id === activeProfileId) || null;
  const flash = (m) => { setNote(m); setTimeout(() => setNote(''), 2500); };
  const closeAll = () => { setOpen(false); setMode(null); setEditId(null); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startCreate = () => { setForm(EMPTY_FORM); setEditId(null); setMode('create'); };
  const startEdit = (p) => {
    setForm({ name: p.name || '', model_id: p.model_id || '', instructions: p.instructions || '', memory: p.memory || '', color: p.color || '' });
    setEditId(p.id); setMode('edit');
  };
  const saveForm = async () => {
    if (!form.name.trim()) return flash('nome é obrigatório');
    const payload = { name: form.name.trim(), model_id: form.model_id.trim() || null, instructions: form.instructions, memory: form.memory, color: form.color || '' };
    if (mode === 'edit' && editId) {
      const r = await updateProfile(editId, payload);
      if (!r.ok) return flash(r.error || 'falhou ao salvar');
      flash('perfil salvo ✓');
    } else {
      const r = await createProfile(payload);
      if (!r.ok) return flash(r.error || 'falhou ao criar');
      if (r.id) await switchProfile(r.id); // acorda o novo perfil
    }
    setForm(EMPTY_FORM); setMode(null); setEditId(null);
  };
  const doCopy = async (id) => {
    const r = await exportProfile(id);
    if (!r.ok) return flash(r.error || 'falhou ao exportar');
    try { await navigator.clipboard.writeText(JSON.stringify(r.profile, null, 2)); flash('Copy setup copiado ✓'); }
    catch { flash('copie manualmente do console'); }
  };
  const doImport = async () => {
    let parsed; try { parsed = JSON.parse(importText); } catch { return flash('JSON inválido'); }
    const r = await importProfile(parsed);
    if (!r.ok) return flash(r.error || 'falhou ao importar');
    setImportText(''); setMode(null); flash('perfil importado ✓');
  };

  const inputCls = 'rounded border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-xs text-apolo-textPrimary outline-none focus:border-apolo-accent';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Perfis / persona"
        title={active ? `Perfil: ${active.name}` : 'Perfis'}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs text-apolo-textTertiary transition hover:bg-apolo-surface1 hover:text-apolo-textPrimary"
      >
        <LetterAvatar name={active ? active.name : 'Perfil'} color={active?.color} size={16} />
        <span className="max-w-[110px] truncate">{active ? active.name : 'Perfil'}</span>
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-2 max-h-[70vh] min-w-[260px] overflow-auto rounded-lg border border-apolo-borderSubtle bg-apolo-surface1 py-1 shadow-lg">
          {mode === null && (
            <>
              {list.length === 0 && (
                <p className="px-3 py-2 text-[11px] text-apolo-textTertiary">Nenhum perfil ainda. Crie o primeiro:</p>
              )}
              {list.map((p) => (
                <div key={p.id} className="flex items-center gap-0.5 pr-1">
                  <button
                    type="button"
                    onClick={async () => { await switchProfile(p.id); closeAll(); }}
                    className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-xs text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                  >
                    <LetterAvatar name={p.name} color={p.color} size={16} active={p.id === activeProfileId} />
                    <span className="min-w-0 truncate">{p.name}</span>
                    {p.id === activeProfileId ? <Check size={12} aria-hidden="true" className="shrink-0 text-apolo-accent" /> : null}
                    {p.model_id ? <span className="ml-auto max-w-[90px] truncate text-[10px] text-apolo-textTertiary">{p.model_id}</span> : null}
                  </button>
                  <button type="button" aria-label={`Editar ${p.name}`} title="Editar (nome, modelo, persona, memória)"
                    onClick={() => startEdit(p)} className="shrink-0 rounded p-1 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary">
                    <Pencil size={12} aria-hidden="true" />
                  </button>
                  <button type="button" aria-label={`Copy setup de ${p.name}`} title="Copy setup (exportar JSON)"
                    onClick={() => doCopy(p.id)} className="shrink-0 rounded p-1 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary">
                    <Copy size={12} aria-hidden="true" />
                  </button>
                </div>
              ))}
              <div className="my-1 border-t border-apolo-borderSubtle" />
              <button type="button" onClick={startCreate} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary">
                <Plus size={12} aria-hidden="true" /> Criar perfil
              </button>
              <button type="button" onClick={() => setMode('import')} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary">
                <ClipboardPaste size={12} aria-hidden="true" /> Importar (Copy setup)
              </button>
            </>
          )}

          {(mode === 'create' || mode === 'edit') && (
            <div className="flex flex-col gap-1.5 px-3 py-2">
              <span className="text-[11px] font-medium text-apolo-textPrimary">{mode === 'edit' ? 'Editar perfil' : 'Novo perfil'}</span>
              <input value={form.name} onChange={set('name')} placeholder="Nome (ex: Trabalho)" autoFocus className={inputCls} />
              <input value={form.model_id} onChange={set('model_id')} placeholder="Modelo (opcional, ex: anthropic/claude-opus-4-8)" className={inputCls} />
              <div className="flex items-center gap-2 pt-0.5">
                <LetterAvatar name={form.name || 'P'} color={form.color} size={18} />
                <span className="text-[10px] text-apolo-textTertiary">Cor do avatar</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Cor do perfil">
                {PROFILE_COLORS.map((hex) => (
                  <button key={hex} type="button" aria-label={`Cor ${hex}`} title="Colorir perfil"
                    onClick={() => setForm((f) => ({ ...f, color: hex }))}
                    className={'h-4 w-4 rounded-full transition hover:scale-110 focus:outline-none focus:ring-2 focus:ring-apolo-accent/40 ' + (form.color === hex ? 'ring-2 ring-apolo-textPrimary/70' : '')}
                    style={{ backgroundColor: hex }} />
                ))}
                {form.color ? (
                  <button type="button" aria-label="Remover cor" title="Sem cor"
                    onClick={() => setForm((f) => ({ ...f, color: '' }))}
                    className="flex h-4 w-4 items-center justify-center rounded-full border border-apolo-borderSubtle text-apolo-textTertiary transition hover:text-apolo-textPrimary">
                    <X size={9} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
              <label className="text-[10px] text-apolo-textTertiary">Instruções / persona (system prompt desta identidade)</label>
              <textarea value={form.instructions} onChange={set('instructions')} rows={3} placeholder="Ex: Você é o assistente de trabalho do Pedro. Tom direto, foco em engenharia…" className={`${inputCls} resize-y`} />
              <label className="text-[10px] text-apolo-textTertiary">Memória (contexto persistente desta persona)</label>
              <textarea value={form.memory} onChange={set('memory')} rows={4} placeholder="Ex: Projeto atual, preferências, decisões, fatos que essa persona deve lembrar…" className={`${inputCls} resize-y`} />
              <div className="flex gap-1">
                <button type="button" onClick={saveForm} className="flex-1 rounded bg-apolo-accent/20 px-2 py-1 text-xs text-apolo-textPrimary transition hover:bg-apolo-accent/30">{mode === 'edit' ? 'Salvar' : 'Criar'}</button>
                <button type="button" onClick={() => { setMode(null); setEditId(null); }} className="rounded px-2 py-1 text-xs text-apolo-textTertiary hover:text-apolo-textPrimary">Cancelar</button>
              </div>
            </div>
          )}

          {mode === 'import' && (
            <div className="flex flex-col gap-1.5 px-3 py-2">
              <textarea value={importText} onChange={(e) => setImportText(e.target.value)} placeholder='Cole o "Copy setup" (JSON)' rows={5} autoFocus className={`${inputCls} font-mono text-[10px] resize-y`} />
              <div className="flex gap-1">
                <button type="button" onClick={doImport} className="flex-1 rounded bg-apolo-accent/20 px-2 py-1 text-xs text-apolo-textPrimary transition hover:bg-apolo-accent/30">Importar</button>
                <button type="button" onClick={() => setMode(null)} className="rounded px-2 py-1 text-xs text-apolo-textTertiary hover:text-apolo-textPrimary">Cancelar</button>
              </div>
            </div>
          )}
          {note && <p className="px-3 py-1 text-[10px] text-apolo-textTertiary">{note}</p>}
        </div>
      )}
    </div>
  );
}
